import * as Card from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { appDialogConfirm } from "@components";
import Identity from "@components/Identity";
import { Avatar } from "@features/Avatar";
import { useNavigate } from "@solidjs/router";
import { useAuth } from "@state/auth";
import type { RecordingWithHost } from "@state/recordings/api";
import { useRecordings } from "@state/recordings/state";
import dayjs from "dayjs";
import { Edit, EllipsisVertical, Trash2 } from "lucide-solid";
import { Show } from "solid-js";
import { Portal } from "solid-js/web";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { useState } from "./state";

export default function (props: { recording: RecordingWithHost }) {
  const navigate = useNavigate();
  const { setEditRecording } = useService(useState)();
  const { deleteRecording } = useService(useRecordings)();
  const { isAdmin } = useService(useAuth)();

  // Programmatic navigation handler
  const handleCardClick = () => {
    navigate(`/recordings/${props.recording.id}`);
  };

  return (
    <Box position="relative">
      <Card.Root
        onClick={handleCardClick}
        w="200px"
        p={3}
        display="grid"
        gap="3"
        h="full"
        alignContent="start"
        position="relative"
        class={css({
          transition: "all 0.2s",
          cursor: "pointer",
          _hover: {
            boxShadow: "1px 1px 5px 1px rgba(0, 0, 0, 0.2)",
          },
        })}
      >
        <Grid gap='3'>
          <Flex justifyContent="space-between">
            <Grid gap="0">
              <Heading truncate textStyle="md">
                {props.recording.title}
              </Heading>
              <Text truncate textStyle="xs">
                {dayjs(props.recording.occurrence_date).format("dddd, D MMMM")}
              </Text>
            </Grid>
          </Flex>
          <Flex justifyContent="space-between">
            <Flex justifyContent="space-between">
              <Show when={isAdmin()}>
                {/* Stopping propagation here is vital.
                  It prevents the Card's onClick from firing when the menu is used.
              */}
                <Box onClick={(e) => e.stopPropagation()}>
                  <Menu.Root
                    onSelect={(details) => {
                      if (details.value === "edit") {
                        setEditRecording(props.recording);
                      }
                      if (details.value === "delete") {
                        appDialogConfirm(
                          () => deleteRecording(props.recording.id),
                          "Are you sure you want to delete this Recording?"
                        )();
                      }
                    }}
                  >
                    <Menu.Trigger
                      asChild={(triggerProps) => (
                        <IconButton
                          variant="outline"
                          size="2xs"
                          {...triggerProps()}
                        >
                          <EllipsisVertical size={14} />
                        </IconButton>
                      )}
                    />
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content>
                          <Menu.Item minH={8} value="edit">
                            <Edit size={14} />
                            <Text textStyle="xs">Edit</Text>
                          </Menu.Item>
                          <Menu.Item minH={8} value="delete" color="error.10">
                            <Trash2 size={14} />
                            <Text textStyle="xs">Delete</Text>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                </Box>
              </Show>
            </Flex>

            <Identity username={props.recording.host?.username} id={props.recording.host?.id} label="Host" rank={props.recording.host?.profile_private?.role} />
          </Flex>
        </Grid>
      </Card.Root>
    </Box>
  );
}
